import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import ViewTimelineRoundedIcon from '@mui/icons-material/ViewTimelineRounded';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  setExtraStructureBoxFields,
  setScheduleView,
} from '@sophocles/features/sites/slices/selectedStructure/actions';
import { extraStructureFields } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { bulkUpsertUserPreferences } from '@sophocles/features/userPreferences/api';
import { saveUserPreferenceItems } from '@sophocles/features/userPreferences/save';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import {
  PREFERENCE_KEY,
  SCHEDULE_VIEW_KEY,
  VALID_SCHEDULE_VIEWS,
} from '@sophocles/views/sites/hooks/useSiteViewPreferences';

export default function StructureCardConfigureDialog({ open, closeDialog }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fields = useSelector(extraStructureFields);

  const [savedValue, , savedValueLoaded] = useUserPreference(PREFERENCE_KEY, []);
  const [savedScheduleView, , savedScheduleViewLoaded] = useUserPreference(SCHEDULE_VIEW_KEY, 'list');
  const preferencesLoaded = savedValueLoaded && savedScheduleViewLoaded;
  const savedFields = useMemo(() => (Array.isArray(savedValue) ? savedValue : []), [savedValue]);
  const savedShown = useMemo(() => new Set(savedFields), [savedFields]);
  const savedScheduleValue = VALID_SCHEDULE_VIEWS.includes(savedScheduleView) ? savedScheduleView : 'list';

  // Local draft while dialog is open
  const [draft, setDraft] = useState(new Set());
  const [draftScheduleView, setDraftScheduleView] = useState('list');

  // Redux-slice sync (extraStructureFields / scheduleView) now runs
  // unconditionally at dashboard mount via useSiteViewPreferences — this
  // dialog only needs the preference values for its own local draft/dirty
  // state below.

  // Initialize drafts from hydrated saved preferences when dialog opens.
  useEffect(() => {
    if (!open || !preferencesLoaded) return;
    setDraft(new Set(savedFields));
    setDraftScheduleView(savedScheduleValue);
  }, [open, preferencesLoaded, savedFields, savedScheduleValue]);

  const handleToggle = key => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const fieldsDirty = useMemo(() => {
    if (!preferencesLoaded) return false;
    if (draft.size !== savedShown.size) return true;
    for (const key of draft) {
      if (!savedShown.has(key)) return true;
    }
    return false;
  }, [draft, savedShown, preferencesLoaded]);

  const scheduleDirty = preferencesLoaded && draftScheduleView !== savedScheduleValue;
  const isDirty = fieldsDirty || scheduleDirty;

  const handleSave = () => {
    if (!preferencesLoaded) return;
    const arr = Array.from(draft);
    const items = [];
    if (fieldsDirty) {
      dispatch(setExtraStructureBoxFields(arr));
      items.push({ key: PREFERENCE_KEY, value: arr });
    }
    if (scheduleDirty) {
      dispatch(setScheduleView(draftScheduleView));
      items.push({ key: SCHEDULE_VIEW_KEY, value: draftScheduleView });
    }
    void saveUserPreferenceItems({ dispatch, items, persist: bulkUpsertUserPreferences });
    closeDialog();
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('configureDisplay.configureYourView')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {/* Schedule display mode */}
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {t('configureDisplay.scheduleDisplay', 'Schedule display')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={draftScheduleView}
              disabled={!preferencesLoaded}
              onChange={(_, value) => {
                if (value != null) setDraftScheduleView(value);
              }}
            >
              <ToggleButton value="list">
                <ViewListRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('sites.scheduleViewList', 'List')}
              </ToggleButton>
              <ToggleButton value="timeline">
                <ViewTimelineRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('sites.scheduleViewTimeline', 'Timeline')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Field visibility checkboxes */}
          <Stack spacing={0.5}>
            {fields
              .map((field, key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.has(key)}
                      disabled={!preferencesLoaded}
                      onChange={() => handleToggle(key)}
                    />
                  }
                  label={field.get('title', key)}
                />
              ))
              .toArray()}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={closeDialog}>
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" disabled={!preferencesLoaded || !isDirty} onClick={handleSave}>
          {t('sites.saveAsDefault', 'Save as default')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
